import React, { useState, useMemo, useRef, useEffect } from "react";
import { Check, ChevronDown, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * MultiSelect — a compact multi-choice picker with search-typeahead.
 *
 * Props:
 *   value       — array of selected option `value`s
 *   onChange    — (nextValues: string[]) => void
 *   options     — [{ value, label, hint? }]
 *   placeholder — string shown when nothing selected
 *   testId      — data-testid root prefix
 *   maxTags     — how many selected chips to show inline before "+N more"
 *
 * Selected values are displayed as chips with × to remove. Unmatched search
 * terms show an empty state. Fully keyboard-friendly (arrow / enter).
 */
export default function MultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = "Select…",
  testId,
  maxTags = 3,
  className,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 20);
    else setQuery("");
  }, [open]);

  const labelMap = useMemo(() => {
    const m = new Map();
    options.forEach((o) => m.set(o.value, o.label));
    return m;
  }, [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        (o.label || "").toLowerCase().includes(q) ||
        (o.hint || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  const removeTag = (v, e) => {
    e.stopPropagation();
    onChange(value.filter((x) => x !== v));
  };

  const shownTags = value.slice(0, maxTags);
  const overflow = Math.max(0, value.length - maxTags);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        data-testid={testId ? `${testId}-trigger` : undefined}
        className={cn(
          "w-full h-9 px-2 py-1 rounded-md border border-gray-200 bg-white text-left text-sm",
          "flex items-center gap-1.5 flex-wrap min-h-[36px]",
          "hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30",
          disabled && "opacity-60 cursor-not-allowed"
        )}
      >
        {value.length === 0 && (
          <span className="text-gray-400 flex-1">{placeholder}</span>
        )}
        {shownTags.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-[11px] text-gray-800"
          >
            {labelMap.get(v) || v}
            <button
              type="button"
              onClick={(e) => removeTag(v, e)}
              className="h-3.5 w-3.5 rounded hover:bg-gray-200 flex items-center justify-center"
              aria-label="Remove"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
        {overflow > 0 && (
          <span className="text-[11px] text-gray-500 px-1">+{overflow}</span>
        )}
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 ml-auto" />
      </button>

      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 rounded-md border border-gray-200 bg-white shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="relative border-b border-gray-100">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full h-9 pl-8 pr-2 text-sm outline-none bg-transparent"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-400 text-center">No matches</p>
            ) : (
              filtered.map((o) => {
                const selected = value.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => toggle(o.value)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors",
                      selected ? "bg-red-50 text-[#9B1C1C]" : "hover:bg-gray-50"
                    )}
                    data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                  >
                    <span
                      className={cn(
                        "h-4 w-4 rounded border flex items-center justify-center shrink-0",
                        selected ? "border-[#9B1C1C] bg-[#9B1C1C] text-white" : "border-gray-300 bg-white"
                      )}
                    >
                      {selected && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 truncate">{o.label}</span>
                    {o.hint && <span className="text-[11px] text-gray-400">{o.hint}</span>}
                  </button>
                );
              })
            )}
          </div>
          {value.length > 0 && (
            <div className="border-t border-gray-100 px-3 py-1.5 flex items-center justify-between text-[11px] text-gray-500">
              <span>{value.length} selected</span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[#9B1C1C] font-semibold hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
